import { Box, Divider, Grid, GridItem, Stack } from '@chakra-ui/react';
import LogoutButton from 'components/LogoutButton';
import { SeasonalEffect } from 'components/SeasonalEffect/SeasonalEffect';
import { SeasonalEffectToggleButton } from 'components/SeasonalEffect/SeasonalEffectToggleButton';
import { useSidebarContext } from 'context/SidebarContext';
import { RiUserSettingsLine } from 'react-icons/ri';
import { NavLink } from 'react-router-dom';

import OeLogo from '../../components/OeLogo';
import { getAvailableGroups, RouteIds, routesByGroup } from '../../configs/routes';
import { NavButton } from './NavButton';
import { NavSectionTitle } from './NavSectionTitle';
import { SidebarToggleButton } from './SidebarToggleButton';

const sectionSpacings = {
  base: 2,
  lg: 6,
};
export const Sidebar = (props: { permissions?: string[]; isLoading: boolean; isMobile?: boolean }) => {
  const { isLoading, permissions = [], isMobile = false } = props;
  const { isCollapsed } = useSidebarContext();

  // The mobile drawer reuses this component, but there is no room to collapse further in there.
  const collapsed = isCollapsed && !isMobile;

  const dashboardRoute = routesByGroup?.general?.routes?.find((route) => route?.id === RouteIds.DASHBOARD)?.to ?? '/app/op/dashboard';

  return (
    // The seasonal effect sits on this non-scrolling wrapper so it stays in view when the nav overflows.
    // minH 0 lets the wrapper shrink to the viewport so the nav content can scroll.
    <Box position="relative" isolation="isolate" bg="default" h="full" minH={0}>
      <SeasonalEffect />
      <Grid
        color="white"
        templateAreas={`"nav-logo"
                  "nav-content"`}
        templateRows={'4rem 1fr'}
        h={'full'}
        width={{ base: 'full', lg: collapsed ? '3.5rem' : 'xs' }}
        maxW={{ base: 'full', sm: 'full' }}
        transition="width 0.3s ease"
        overflow={'hidden'}
        gap="4"
        pt={collapsed ? '1' : '4'}
        px={collapsed ? '1' : '3'}
      >
        <GridItem
          area={'nav-logo'}
          display="flex"
          alignItems="center"
          justifyContent={collapsed ? 'flex-start' : 'space-between'}
          paddingInlineStart={0}
          gap="2"
        >
          {!collapsed && (
            <Box as={NavLink} to={dashboardRoute} flex="1" minW="0">
              <OeLogo width="10rem" />
            </Box>
          )}
          {!isMobile && <SidebarToggleButton />}
        </GridItem>
        {/* Only this part scrolls, so the logo row stays in place */}
        <Grid
          gridArea={'nav-content'}
          templateAreas={`"nav-main"
                    "nav-footer"`}
          templateRows={'1fr auto'}
          gap="4"
          overflowY={'auto'}
          overflowX={'hidden'}
          sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
          pb={collapsed ? '1' : '4'}
        >
          <GridItem area={'nav-main'}>
            <Stack w={'full'} spacing={collapsed ? 4 : sectionSpacings}>
              {getAvailableGroups(permissions).map((group, groupIndex) => {
                return (
                  <Stack key={`sidebar-group-${groupIndex}`}>
                    {collapsed ? (
                      groupIndex > 0 ? (
                        <Divider borderColor="whiteAlpha.400" mb="2" />
                      ) : null
                    ) : (
                      <NavSectionTitle label={`sidebar:${group.label}`} mb={'2'} />
                    )}
                    <Stack gap="0.5">
                      {group?.routes?.map((route, index) => {
                        const navigationButton =
                          route.label === 'operations.whitelabel_organizations' ? null : (
                            <NavButton
                              key={`sidebar-route-${index}`}
                              isLoading={isLoading}
                              isCollapsed={collapsed}
                              to={route.to}
                              label={route.label}
                              icon={route.icon}
                            />
                          );
                        return navigationButton;
                      })}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </GridItem>
          <GridItem area={'nav-footer'}>
            <Divider borderColor="bg-white" />
            <Grid gap="1" mt="4">
              <Box>
                <NavButton
                  isLoading={isLoading}
                  isCollapsed={collapsed}
                  to={'/app/op/personal-settings'}
                  label="settings.personal_settings"
                  icon={RiUserSettingsLine}
                />
              </Box>
              <SeasonalEffectToggleButton isCollapsed={collapsed} />
              <Stack spacing={2}>
                <LogoutButton isSidebar isCollapsed={collapsed} />
              </Stack>
            </Grid>
          </GridItem>
        </Grid>
      </Grid>
    </Box>
  );
};
